<template>
  <ClientOnly>
    <component :is="SafeModeBanner" v-if="clientPreview && isSafeMode" />
    <component :is="Toolbar" v-if="clientPreview" />
  </ClientOnly>
  <div class="w-100 relative md:flex" :class="clientPreview ? 'h-[calc(100vh-52px)]' : ''">
    <ClientOnly>
      <component :is="SettingsToolbar" v-if="clientPreview && disableActions" class="flex-shrink-0" />
    </ClientOnly>

    <component
      :is="SiteConfigurationDrawer"
      v-if="siteConfigurationDrawerOpen && clientPreview"
      class="flex-shrink-0 bg-white font-editor border-r border-gray-300 overflow-visible"
    />

    <div
      ref="contentRef"
      class="flex-1 w-full relative"
      :class="
        clientPreview
          ? ['overflow-auto flex flex-col', isMobilePreview ? 'bg-editor-body-bg' : 'bg-white']
          : 'overflow-visible bg-white'
      "
    >
      <Body
        class="font-body bg-editor-body-bg"
        :class="[bodyClass, { 'overflow-hidden': clientPreview }]"
        :style="currentFont"
      />
      <UiNotifications />
      <VitePwaManifest />
      <NuxtLoadingIndicator color="repeating-linear-gradient(to right, #008ebd 0%,#80dfff 50%,#e0f7ff 100%)" />
      <div
        id="app-container"
        ref="previewContainerEl"
        :style="
          isMobilePreview
            ? {
                width: previewWidth,
                maxWidth: '100%',
                transform: 'translateZ(0)',
                height: '99%',
                overflow: 'clip',
                display: 'flex',
                flexDirection: 'column',
                '--viewport-height': '90dvh',
                isolation: 'isolate',
              }
            : { isolation: 'isolate' }
        "
        :class="isMobilePreview ? 'mx-auto bg-white my-auto shadow-md @container' : '@container'"
        data-testid="editor-preview-container"
      >
        <template v-if="isMobilePreview">
          <div style="flex: 1; min-height: 0; overflow-y: auto">
            <NuxtLayout>
              <NuxtPage />
            </NuxtLayout>
          </div>
        </template>
        <template v-else>
          <NuxtLayout>
            <NuxtPage />
          </NuxtLayout>
        </template>
      </div>
    </div>

    <component
      :is="BlocksConfigurationDrawer"
      v-if="blocksConfigurationDrawerOpen && clientPreview"
      class="flex-shrink-0 bg-white font-editor border-l border-gray-300 overflow-y-auto"
    />
  </div>
  <ClientOnly>
    <component :is="PageModal" v-if="clientPreview" />
    <component :is="UnlinkCategoryModal" v-if="clientPreview" />
    <component :is="ResetProductPageModal" v-if="clientPreview" />
    <component :is="AddBlockPopoverComponent" v-if="clientPreview" />
  </ClientOnly>
</template>

<script setup lang="ts">
import { useMediaQuery } from '@vueuse/core';
import { isCssUrl, isJsUrl } from '~/utils/assets';
import { categoryGetters } from '@plentymarkets/shop-api';

const bodyClass = ref('');
const route = useRoute();
const { locale } = useI18n();
const { disableActions } = useEditor();
const { siteConfigurationDrawerOpen, blocksConfigurationDrawerOpen, currentFont } = useSiteConfiguration();
const { setStaticPageMeta } = useUrlPageMeta();
const { isInEditorClient, isMobilePreview, previewWidth } = useEditorState();

const isLargeScreen = useMediaQuery('(min-width: 1024px)');
const clientPreview = computed(() => isInEditorClient.value && isLargeScreen.value);
const contentRef = ref<HTMLElement | null>(null);
const previewContainerEl = ref<HTMLElement | null>(null);
provide('previewContainer', previewContainerEl);

const { getSetting: getFavicon } = useSiteSettings('favicon');
const { getSetting: getOgTitle } = useSiteSettings('ogTitle');
const { getSetting: getOgImage } = useSiteSettings('ogImage');
const { getSetting: getMetaTitle } = useSiteSettings('metaTitle');
const { getSetting: getMetaDescription } = useSiteSettings('metaDescription');
const { getSetting: getMetaKeywords } = useSiteSettings('metaKeywords');
const { getSetting: getRobots } = useSiteSettings('robots');
const { getSetting: getPrimaryColor } = useSiteSettings('primaryColor');
const { getSetting: customAssetsSafeMode } = useSiteSettings('customAssetsSafeMode');

const { data: productsCatalog } = useProducts();

const category = computed(() => productsCatalog.value?.category);
const isCategoryPage = computed(() => route.meta?.type === 'category' && !!category.value);

const { getAssetsOfType } = useCustomAssets();

const isSafeMode = computed(() => customAssetsSafeMode());

const getCategoryMetaTitle = () => {
  if (isCategoryPage.value) {
    const categoryMetaTitle = categoryGetters.getMetaTitle(category.value);
    if (categoryMetaTitle) return categoryMetaTitle;
  }
  return getMetaTitle();
};

const getCategoryMetaDescription = () => {
  if (isCategoryPage.value) {
    const categoryMetaDescription = categoryGetters.getMetaDescription(category.value);
    if (categoryMetaDescription) return categoryMetaDescription;
  }
  return getMetaDescription();
};

const getCategoryMetaKeywords = () => {
  if (isCategoryPage.value) {
    const categoryMetaKeywords = categoryGetters.getMetaKeywords(category.value);
    if (categoryMetaKeywords) return categoryMetaKeywords;
  }
  return getMetaKeywords();
};

const getCategoryOgTitle = () => {
  if (isCategoryPage.value) {
    const categoryMetaTitle = categoryGetters.getMetaTitle(category.value);
    if (categoryMetaTitle) return categoryMetaTitle;
  }
  return getOgTitle() || getMetaTitle();
};

const title = ref(getCategoryMetaTitle());
const ogTitle = ref(getCategoryOgTitle());
const ogImage = ref(getOgImage());
const ogDescription = ref(getCategoryMetaDescription());
const description = ref(getCategoryMetaDescription());
const keywords = ref(getCategoryMetaKeywords());
const robots = ref(getRobots());
const fav = ref(getFavicon());
const themeColor = ref(getPrimaryColor());

const cssAssets = computed(() => (isSafeMode.value ? [] : getAssetsOfType('css')));

const jsHeadAssets = computed(() =>
  isSafeMode.value
    ? []
    : getAssetsOfType('javascript').filter(
        (asset) => asset.isActive && (asset.placement === 'head_end' || !asset.placement),
      ),
);

const jsFooterAssets = computed(() =>
  isSafeMode.value
    ? []
    : getAssetsOfType('javascript').filter((asset) => asset.isActive && asset.placement === 'body_end'),
);

const metaAssets = computed(() => (isSafeMode.value ? [] : getAssetsOfType('meta').filter((asset) => asset.isActive)));
const cssExternalAssets = computed(() =>
  isSafeMode.value ? [] : getAssetsOfType('external').filter((asset) => isCssUrl(asset.content)),
);
const jsExternalAssets = computed(() =>
  isSafeMode.value ? [] : getAssetsOfType('external').filter((asset) => asset.isActive && isJsUrl(asset.content)),
);

watchEffect(() => {
  title.value = getCategoryMetaTitle();
  ogTitle.value = getCategoryOgTitle();
  ogImage.value = getOgImage();
  ogDescription.value = getCategoryMetaDescription();
  description.value = getCategoryMetaDescription();
  keywords.value = getCategoryMetaKeywords();
  robots.value = getRobots();
  fav.value = getFavicon();
  themeColor.value = getPrimaryColor();
});

useSeoMeta({
  title: () => title.value,
  ogTitle: () => ogTitle.value,
  ogImage: () => ogImage.value,
  ogDescription: () => ogDescription.value,
  description: () => description.value,
  robots: () => robots.value,
  themeColor: () => themeColor.value,
  generator: 'plentymarkets',
});

const localeHead = useLocaleHead();

useHead({
  htmlAttrs: {
    lang: () => localeHead.value.htmlAttrs.lang,
    dir: () => localeHead.value.htmlAttrs.dir as 'ltr' | 'rtl' | 'auto' | undefined,
  },
  link: () => [
    { rel: 'icon', href: fav.value },
    { rel: 'apple-touch-icon', href: fav.value },
    ...cssExternalAssets.value.map((asset, index) => ({
      key: `external-css-${asset.uuid ?? index}`,
      rel: 'stylesheet' as const,
      media: asset.isActive ? 'all' : 'not all',
      href: asset.content,
    })),
  ],
  meta: () => [
    { name: 'keywords', content: keywords.value },
    ...metaAssets.value
      .filter((asset) => asset.name && asset.content)
      .map((asset) => ({
        key: `custom-meta-${asset.uuid}`,
        name: asset.name,
        content: asset.content,
      })),
  ],
  style: () =>
    cssAssets.value.map((asset) => ({
      key: `custom-css-${asset.uuid}-o${asset.order ?? 0}`,
      textContent: asset.content,
      media: asset.isActive ? 'all' : 'not all',
      tagPriority: 100 + (asset.order ?? 0),
    })),
});

if (import.meta.client) {
  useHead({
    script: () => [
      ...jsHeadAssets.value.map((asset) => ({
        key: `custom-js-${asset.uuid}`,
        innerHTML: asset.content,
      })),
      ...jsFooterAssets.value.map((asset) => ({
        key: `custom-js-${asset.uuid}-footer`,
        innerHTML: asset.content,
        tagPosition: 'bodyClose',
      })),
      ...jsExternalAssets.value.map((asset) => ({
        key: `external-js-${asset.uuid}`,
        src: asset.content,
        defer: true,
      })),
    ],
  });

  watch(
    () => route.path,
    () => {
      if (clientPreview.value) contentRef.value?.scrollTo({ top: 0 });
    },
  );
}

if (route?.meta.pageType === 'static') setStaticPageMeta();
usePageTitle();

onMounted(() => {
  bodyClass.value = 'hydrated'; // Need this class for cypress testing
});

const SafeModeBanner = defineAsyncComponent(() => import('~/components/SafeModeBanner/SafeModeBanner.vue'));
const Toolbar = defineAsyncComponent(() => import('~/components/ui/Toolbar/Toolbar.vue'));
const SettingsToolbar = defineAsyncComponent(() => import('~/components/SettingsToolbar/SettingsToolbar.vue'));
const SiteConfigurationDrawer = defineAsyncComponent(
  () => import('~/components/SiteConfigurationDrawer/SiteConfigurationDrawer.vue'),
);
const BlocksConfigurationDrawer = defineAsyncComponent(
  () => import('~/components/SiteConfigurationDrawer/BlocksConfigurationDrawer.vue'),
);
const PageModal = defineAsyncComponent(() => import('~/components/ui/PageModal/PageModal.vue'));
const UnlinkCategoryModal = defineAsyncComponent(
  () => import('~/components/ui/UnlinkCategoryModal/UnlinkCategoryModal.vue'),
);
const ResetProductPageModal = defineAsyncComponent(
  () => import('~/components/ui/ResetProductPageModal/ResetProductPageModal.vue'),
);
const AddBlockPopoverComponent = defineAsyncComponent(() => import('~/components/AddBlockPopover/AddBlockPopover.vue'));
</script>

<style lang="scss">
@use '~/assets/style.scss';
</style>
